'use client';

import { useState, useEffect, useCallback } from 'react';
import { getTranslation, type Language, type TranslationKey } from '@/lib/translations';

export function useTranslation() {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }

    // Listen for language changes
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') as Language;
      if (newLanguage) {
        setLanguage(newLanguage);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Custom event for same-tab updates
    window.addEventListener('languageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  // Use useCallback to ensure t() updates when language changes
  const t = useCallback((key: TranslationKey): string => {
    return getTranslation(language, key);
  }, [language]);

  const changeLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('languageChange'));
  };

  return { t, language, changeLanguage };
}
